import {
  AbsoluteCenter,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  PinInput,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import {
  CustomDrawerBackButton,
  CustomDrawerClosePrimaryButton,
  CustomDrawerFooter,
  CustomFormErrorMessage,
  CustomPinInputField,
  PrimaryButton,
  StatusBox,
} from '../../../components/custom';
// import GetReceiptButton from '../../../components/custom/CustomButtons/GetReceiptButton';
import { SaveIcon } from '../../../components/icons';
import Transaction2faStep from '../../../components/transactions/Transaction2faStep';
import { transactionPinValidation } from '../../../constants/validationConstants';
import { TTransaction2faType } from '../../../store/slices/transaction2fa/transaction2faTypes';
import BeneficiarySummary, {
  IBillDetails,
} from '../Beneficiaries/BeneficiarySummary';

interface TransactionPinFormProps {
  isSuccess?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onSubmit: (pinValues: { pin: string }) => void;
  goToReceipt?: () => void;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  statusBoxTitle?: string;
  statusBoxText?: string;
  showGetReceiptButton?: boolean;
  showBackButton?: boolean;
  billDetails?: IBillDetails;
  handleClose?: () => void;
  displaySaveBeneficiary?: boolean;
  twoFactor?: { type: TTransaction2faType; amountKobo: number };
}

export const TransactionPinForm = ({
  isSuccess,
  isLoading,
  isError,
  onSubmit,
  goToReceipt,
  step,
  setStep,
  statusBoxTitle = 'Bill Payment Successful!',
  statusBoxText = 'Your bill payment was successful. Thank you for choosing 9jaPay!',
  showGetReceiptButton = true,
  showBackButton = true,
  billDetails,
  handleClose,
  displaySaveBeneficiary = true,
  twoFactor,
}: TransactionPinFormProps) => {
  const [twoFactorPassed, setTwoFactorPassed] = useState(false);
  const initialValues = {
    pin: '',
  };

  const validationSchema = Yup.object().shape({
    pin: transactionPinValidation,
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      onSubmit({ pin: values.pin });
    },
  });

  const { errors, touched, dirty, values, handleSubmit, setFieldValue } =
    formik;

  const handleGoBack = () => {
    setStep((currentStep) => currentStep - 1);
  };

  // const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const [isBeneficiaryDrawerOpen, setIsBeneficiaryDrawerOpen] = useState(false);

  const showTwoFactor = Boolean(twoFactor) && !twoFactorPassed && !isSuccess;

  if (showTwoFactor && twoFactor) {
    return (
      <>
        {showBackButton && <CustomDrawerBackButton onClick={handleGoBack} />}
        <Transaction2faStep
          type={twoFactor.type}
          amountKobo={twoFactor.amountKobo}
          onProceed={() => setTwoFactorPassed(true)}
        />
      </>
    );
  }

  return (
    <>
      {!isSuccess && showBackButton && (
        <CustomDrawerBackButton onClick={handleGoBack} />
      )}
      {isSuccess ? (
        <>
          <AbsoluteCenter>
            <StatusBox variant="success" title={statusBoxTitle}>
              {statusBoxText}
            </StatusBox>
            {/* {showGetReceiptButton && (
            <Box px={24} py={10}>
              <GetReceiptButton
                bg="#E0E3E6"
                color="#000000"
                onClick={() => {
                  goToReceipt?.();
                }}
              >
                Get receipt
              </GetReceiptButton>
            </Box>
          )} */}
            {displaySaveBeneficiary && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  leftIcon={<SaveIcon />}
                  sx={{
                    border: '1px solid #EBECED',
                    fontWeight: '400',
                    borderRadius: '8px',
                  }}
                  onClick={() => {
                    setIsBeneficiaryDrawerOpen(true);
                  }}
                >
                  Save Beneficiary
                </Button>
              </Box>
            )}
          </AbsoluteCenter>
          <CustomDrawerClosePrimaryButton>Done</CustomDrawerClosePrimaryButton>
        </>
      ) : (
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={10}>
              <Box>
                <Heading
                  as="h1"
                  fontWeight={500}
                  fontSize="1rem"
                  lineHeight="133%"
                  mb={2}
                >
                  Transaction PIN
                </Heading>
                <Text color="#656B76" fontSize=".875rem">
                  Please enter the transaction PIN
                </Text>
              </Box>
              <FormControl
                id="pin"
                isInvalid={Boolean(touched.pin && errors.pin)}
              >
                <FormLabel fontSize=".875rem">Enter PIN</FormLabel>
                <HStack>
                  <PinInput
                    mask
                    value={values.pin}
                    onChange={(value) => setFieldValue('pin', value)}
                    isInvalid={Boolean(touched.pin && errors.pin)}
                  >
                    <CustomPinInputField pinInputVariant="transactionPin" />
                    <CustomPinInputField pinInputVariant="transactionPin" />
                    <CustomPinInputField pinInputVariant="transactionPin" />
                    <CustomPinInputField pinInputVariant="transactionPin" />
                  </PinInput>
                </HStack>
                <CustomFormErrorMessage>
                  {touched.pin && errors.pin}
                </CustomFormErrorMessage>
              </FormControl>

              <CustomDrawerFooter>
                <PrimaryButton
                  w="full"
                  type="submit"
                  isDisabled={!dirty}
                  isLoading={isLoading}
                >
                  Continue
                </PrimaryButton>
              </CustomDrawerFooter>
            </Stack>
          </Form>
        </FormikProvider>
      )}
      <BeneficiarySummary
        isBeneficiaryDrawerOpen={isBeneficiaryDrawerOpen}
        setIsBeneficiaryDrawerOpen={setIsBeneficiaryDrawerOpen}
        billClose={handleClose}
        billDetails={billDetails}
      />
    </>
  );
};
